import { Company } from "@models/companies/company";
import { Entity } from "@models/companies/entity";
import { Document } from "@models/documents/document";
import { Relationship, TrusteeRelationshipDetails } from "@models/relationships/relationship";
import { RelationshipType, RelationshipTypeLabels } from "@models/relationships/relationship-type.enum";
import { TrustSubtypesLabelsDictionary } from "@models/trusts/trusts-enums";
import { ChangeDictionaryHelper } from "@models/ui-models/change-dictionary-helper.model";
import { IPDFSection } from "@models/ui-models/pdf-section.interface";
import { NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { Guid } from "@shared/utils/guid.helper";
import { BaseTrustEstablishmentFormComponent } from "../../asic-forms/trusts";
import { getTrustEstablishmentChangeType } from "../../asic-forms/trusts/constants/trust-establishment-texts";
import { TrustEstablishmentBase } from "./trust-establishment-base.model";

export class TrustDiscretionaryEstablishment extends TrustEstablishmentBase {
  static override $type = 'TrustDiscretionaryEstablishment';
  settlor: Relationship;
  appointors: Relationship[];
  beneficiaries: Relationship[];
  settledSum: number;

  constructor(data: Partial<TrustDiscretionaryEstablishment> = {}) {
    super(data);

    this.settlor = new Relationship(data.settlor);
    this.appointors = data.appointors?.map((appointor) => new Relationship(appointor)) ?? [];
    this.beneficiaries = (data.beneficiaries ?? []).map((relationship) => new Relationship(relationship));
    this.settledSum = data.settledSum ?? 0;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    const instance = (modalRef.componentInstance as BaseTrustEstablishmentFormComponent<any, TrustDiscretionaryEstablishment>);

    instance.documentId = document.documentId;
    instance.entityId = document.entityId!;
    instance.isEdit = true;
    instance.formModel = new TrustDiscretionaryEstablishment(this);

    return this.fillModalWithCompany(modalRef, actualCompany, true);
  }

  override fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit: boolean): NgbModalRef {
    const instance = modalRef.componentInstance as BaseTrustEstablishmentFormComponent<any, TrustDiscretionaryEstablishment>;

    instance.entityId = company?.entityId ?? Guid.EmptyGuid;
    instance.trustType = this.trustType;
    instance.trustSubtype = this.subType;

    return modalRef;
  }

  override toDictionary(entity): { key: string, value: string }[] {
    const dict = new ChangeDictionaryHelper();

    const relationships = [
      this.settlor,
      ...this.appointors,
      ...this.trustees,
      ...this.beneficiaries
    ];

    dict
      .add('Trust Name', this.trustName)
      .add('Trust Type', TrustSubtypesLabelsDictionary[this.subType])
      .addJurisdiction('Jurisdiction', this.jurisdiction!)
      .addMoney('Settled Sum', this.settledSum)
      .add('Meeting Address', this.meetingAddress.normalizedFullAddress);

    relationships.forEach((relationship) => {
      const name = relationship.individualDataOverride?.fullName ?? relationship.entityDataOverride?.name;
      const acn = relationship.entityDataOverride?.entityNumber;
      const address = (relationship.individualDataOverride?.address ?? relationship.entityDataOverride?.registeredAddress)?.normalizedFullAddress;

      dict.addDivider();

      if (relationship.entityDataOverride) {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Company Name`, name)
          .addAcn('ACN', acn)
          .add('Registration Address', address);

        if (relationship.details && relationship.details.$type == TrusteeRelationshipDetails.$type && (relationship.details as TrusteeRelationshipDetails | null)?.authorisedSignatories?.length) {
          const details = relationship.details as TrusteeRelationshipDetails;
          const authSignatories = details.authorisedSignatories
            .map((signatory) => signatory.fullName.trim())
            .filter(Boolean)
            .join('\n');

          dict.add('Authorising Signatories', authSignatories);
        }
      } else {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Name`, name)
          .add('Address', address);
      }
    });

    return dict.dictionary;
  }

  override prepareToPdf(entity: Entity): IPDFSection[] {
    const dict = new ChangeDictionaryHelper();

    dict
      .addSection('Trust Info')
      .add('Trust Name', this.trustName)
      .add('Trust Type', TrustSubtypesLabelsDictionary[this.subType])
      .addJurisdiction('Jurisdiction', this.jurisdiction!)
      .addMoney('Settled Sum', this.settledSum)
      .add('Meeting Address', this.meetingAddress.normalizedFullAddress);

    const relationships = [
      this.settlor,
      ...this.appointors,
      ...this.trustees,
      ...this.beneficiaries
    ];

    relationships.forEach((relationship, index) => {
      const name = relationship.individualDataOverride?.fullName ?? relationship.entityDataOverride?.name;
      const acn = relationship.entityDataOverride?.entityNumber;
      const address = (relationship.individualDataOverride?.address ?? relationship.entityDataOverride?.registeredAddress)?.normalizedFullAddress;

      const type = relationship.type;
      const prevType: RelationshipType | undefined = relationships[index - 1]?.type;

      if (type !== prevType) {
        dict.addSection(RelationshipTypeLabels[relationship.type]);
      } else {
        dict.add('', ''); // empty line
      }

      if (relationship.entityDataOverride) {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Company Name`, name)
          .addAcn('ACN', acn)
          .add('Registration Address', address);
      } else if (relationship.individualDataOverride) {
        dict
          .add(`${ RelationshipTypeLabels[relationship.type] } Name`, name)
          .add('Address', address);
      }
    });

    return dict.sections;
  }

  override prepareToRequest(): TrustDiscretionaryEstablishment {
    const payload = super.prepareToRequest() as TrustDiscretionaryEstablishment;

    payload.trustees = payload.trustees.map((relationship) => {
      if (relationship.entityDataOverride) {
        const company = relationship.entityDataOverride as Company;

        // @ts-ignore
        delete company.nextAnnualReviewDate;
      }

      return Relationship.prepareToRequest(relationship);
    });

    return payload;
  }

  override buildPreviewType(): string {
    return getTrustEstablishmentChangeType(this.subType)
      .replace('t:', '')
      .replaceAll('-', '');
  }
}
